import { ReactNode, useEffect } from "react";
import { CSS } from "@stitches/react";
import { Icon } from "../Icons/LucideIcons";
import {
  ScrollButton,
  ScrollContainer,
  ScrollWrapper,
} from "./__styles__/HorizontalScrollBar";
import { useHorizontalScrollBar } from "./useHorizontalScrollBar";

interface HorizontalScrollBarProps {
  children: ReactNode;
  scrollKey?: string;
  selectedSelector?: string | false;
  containerCss?: CSS;
  buttonCss?: CSS;
  "data-testid"?: string;
}

export const HorizontalScrollBar = ({
  children,
  scrollKey,
  selectedSelector = "[aria-selected='true']",
  containerCss,
  buttonCss,
  "data-testid": testId,
}: HorizontalScrollBarProps) => {
  const {
    containerRef,
    canScrollLeft,
    canScrollRight,
    isScrollable,
    scrollLeft,
    scrollRight,
  } = useHorizontalScrollBar();

  useEffect(() => {
    if (!containerRef.current || !isScrollable || !selectedSelector) return;

    const selected = containerRef.current.querySelector(selectedSelector);
    if (selected) {
      selected.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [scrollKey, isScrollable, containerRef, selectedSelector]);

  return (
    <ScrollWrapper>
      {isScrollable && canScrollLeft && (
        <ScrollButton
          onClick={scrollLeft}
          aria-label="Scroll left"
          direction="left"
          css={buttonCss}
        >
          <Icon iconName="chevron-left" color="contentSecondary" size={16} />
        </ScrollButton>
      )}

      <ScrollContainer
        ref={containerRef}
        css={containerCss}
        data-testid={testId}
      >
        {children}
      </ScrollContainer>

      {isScrollable && canScrollRight && (
        <ScrollButton
          onClick={scrollRight}
          aria-label="Scroll right"
          direction="right"
          css={buttonCss}
        >
          <Icon iconName="chevron-right" color="contentSecondary" size={16} />
        </ScrollButton>
      )}
    </ScrollWrapper>
  );
};
